import { WORKER_SECTION_HERO_XML } from '../../../shared/workerSectionArt.js'
import { ANNOUNCEMENT_ICON_XML as ICON } from '../../../shared/announcementIcons.js'
import { HOW_IT_WORKS_ART_XML } from '../../../shared/howItWorksArt.js'
import Seo from '../components/Seo'
import MarketingNav from '../components/MarketingNav'
import MarketingFooter from '../components/MarketingFooter'
import PageHero from '../components/ds/PageHero'
import Section from '../components/ds/Section'
import SectionHead, { H2 } from '../components/ds/SectionHead'
import CtaBand from '../components/ds/CtaBand'
import BankStrip from '../components/ds/BankStrip'
import Button from '../components/ds/Button'
import Art from '../components/ds/Art'
import Prose from '../components/ds/Prose'
import PhoneFrame, { AppScreen } from '../components/ds/PhoneFrame'
import FaqAccordion from '../components/ds/FaqAccordion'
import { findWorkJsonLd } from '../lib/landingJsonLd'

// /getting-paid — the highest intent page on the worker side, and the one most
// likely to win a featured snippet, because "when do locums get paid" is a real
// question nobody answers straight.
//
// ⚠️ EVERY FACTUAL CLAIM ON THIS PAGE IS LOAD BEARING (spec §8). Founder
// 2026-10-03: no "usually" on payout speed. The bold claim ("the moment you clock
// out", true with INSTANT_TIMESHEET_APPROVAL_ENABLED on, checked in prod that
// day) is ALWAYS followed by the exception sentence: overtime, a timesheet under
// review or a payment that needs checking can take up to 24 hours. That sentence
// is what keeps the claim true on a bad day. Never drop it, and if instant
// approval is ever switched off, this copy is false and must change. Before changing a
// number here, read docs/PAYMENTS.md and
// servers/onboarding/src/lib/workerPay.js, which is the one boundary deciding
// what a worker sees.
//
// ⛔ The worker never sees the all in price a workplace pays. Nothing on this
// page hints at it.

const FAQS = [
  { q: 'When does the money actually arrive?',
    a: 'The moment you clock out. Your pay lands straight in your TempDash wallet. Overtime, a timesheet under review or a payment that needs checking can take up to 24 hours to clear.' },
  { q: 'What does it cost to get paid?',
    a: 'Joining is free and accepting a shift is free. The rate you see is the rate you are paid for every hour you work.' },
  { q: 'Do I have to invoice anyone?',
    a: 'No. Your timesheet comes from your own clock in and clock out, and we handle the rest. You can download a record of every shift whenever you need one.' },
  { q: 'What about tax?',
    a: "You’re self employed, so your tax is yours to sort out. We give you a clear record of everything you’ve earned to make that straightforward." },
  { q: 'What if the workplace says I worked different hours?',
    a: "They can raise it within a day of the shift. If the two accounts don’t match, we look at it rather than taking either side on trust, and you can tell us what happened." },
  { q: 'What if my shift finishes early?',
    a: "If the workplace sent you home, you’re paid for the shift you agreed to." },
]

export default function GettingPaid() {
  return (
    <div className="bg-canvas">
      <Seo
        title="Getting paid for locum shifts"
        description="When the money arrives and where it sits. Clock out and your pay lands in your wallet, ready to move to your bank in minutes."
        path="/getting-paid"
        jsonLd={findWorkJsonLd(FAQS)}
      />
      <MarketingNav />

      {/* ⚠️ The ONE main landmark. Without it every <section> on the page
          sits outside any landmark and a screen reader user navigating by
          landmark cannot reach the content at all. The skip link in
          MarketingNav targets this id. */}
      <main id="main" tabIndex={-1} className="outline-none">

      <PageHero
        art={WORKER_SECTION_HERO_XML.gettingPaid}
        eyebrow="Find work"
        title="Paid the day you work."
        lead="Not in 30 days. Not when someone remembers to run payroll. When you clock out."
      >
        <Button href="/signup?for=work">Join TempDash</Button>
      </PageHero>

      <Section ground="canvas">
        <div className="flex flex-col items-center gap-14 md:flex-row md:justify-between md:gap-20">
          <div className="max-w-[520px]">
            <SectionHead eyebrow="The short version" title={<>Clock out, and it’s yours.</>} />
            <p className="mt-5 text-[19px] leading-relaxed text-ink-muted">
              Your pay lands in your TempDash wallet the moment you clock out. Overtime, a timesheet under review or a payment that needs checking can take up to 24 hours to clear. From there you move it to your bank whenever you want, in minutes,
              or leave it where it is.
            </p>
            <p className="mt-4 text-[19px] leading-relaxed text-ink-muted">
              There’s no invoice to raise, no timesheet to post and nobody to chase.
            </p>
          </div>
          <PhoneFrame><AppScreen id="worker-wallet" /></PhoneFrame>
        </div>
      </Section>

      <Section ground="surface">
        <BankStrip footnote="* Instant transfers can occasionally take longer, depending on your bank." />
      </Section>

      <Section ground="surface" width="text">
        <div className="space-y-12">
          <Prose id="what-it-costs" art={ICON.payday} title="What it costs you">
            <p>
              Nothing to join. Nothing to accept a shift.
            </p>
            <p>
              The rate you see on a shift is the rate you are paid for the hours you work. There is
              no joining fee, no subscription and no cut taken out of it at the end.
            </p>
          </Prose>

          <Prose id="your-hours" art={ICON.rate} title="How your hours are worked out">
            <p>
              From your own clock in and clock out. You are not relying on somebody at the
              workplace writing it down correctly, and you are not filling in a timesheet at the
              end of the week trying to remember.
            </p>
            <p>
              If you stay later than the shift you agreed to, that goes on your timesheet too.
            </p>
          </Prose>

          <Prose id="if-it-is-wrong" art={ICON.support} title="If someone disagrees with your hours">
            <p>
              A workplace has a day after the shift to raise it. If they do, and the two accounts
              do not match, we look at what actually happened rather than taking either side on
              trust. You get to say your piece.
            </p>
            <p>
              If nobody raises anything, your timesheet stands and you keep your money.
            </p>
          </Prose>

          <Prose id="self-employed" art={ICON.compliance} title="You are self employed">
            <p>
              You are not employed by TempDash and not employed by the workplace. You choose every
              shift you take, and you’re never obliged to take one.
            </p>
            <p>
              That means your tax is yours to handle. We keep a clear record of every shift and
              every payment, which you can download whenever you need it.
            </p>
          </Prose>

          <Prose id="your-money" art={ICON.wallet} title="Where your money sits">
            <p>
              In your TempDash wallet until you move it. The money itself is held by a regulated
              payments provider, not by us, and it’s yours the moment it lands.
            </p>
          </Prose>
        </div>
      </Section>

      <Section ground="navy">
        <div className="md:flex md:items-center md:gap-16">
          <Art xml={HOW_IT_WORKS_ART_XML.paid} className="h-[96px] w-[96px] shrink-0" />
          <div className="mt-6 md:mt-0">
            <H2 size="md">
              The reason people move across.
            </H2>
            <p className="mt-4 max-w-[640px] text-[19px] leading-relaxed text-white/75">
              Most locum work still pays weeks late, and only after somebody has chased it. Being
              paid on the day you worked is not a perk. It is your money, when you earned it.
            </p>
          </div>
        </div>
      </Section>

      <Section ground="surface" width="text">
        <H2>Questions, answered.</H2>
        <FaqAccordion items={FAQS} className="mt-10" />
      </Section>

      <CtaBand title="Start getting paid properly.">
        <Button href="/signup?for=work">Join TempDash</Button>
      </CtaBand>

      </main>

      <MarketingFooter />
    </div>
  )
}
